import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { getFieldByLang, formatPrice } from "@/shared/utils/helpers";
import Center from "../Base/Center";
import Icon from "../Icon/Icon";
import { useSetModal } from "@/shared/atoms/useModal";
import { CartItem as ICartItem } from "@/shared/utils/interfaces";
import { cn } from "@/shared/utils/cn";

interface CartItemProps {
  watchMode?: boolean;
  isSelected?: boolean;
  cartItem: ICartItem;
  index: number;
  total: number;
  onSelect?: () => void;
  onDelete?: () => void;
  onAdd?: () => void;
  onDeleteNote?: () => void;
}

const CartItem = forwardRef<HTMLDivElement, CartItemProps>(
  (
    {
      watchMode = false,
      isSelected,
      cartItem,
      index,
      total,
      onSelect,
      onDelete,
      onAdd,
      onDeleteNote,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const setModal = useSetModal();

    return (
      <div
        ref={ref}
        className={cn(
          "relative min-h-12 cursor-pointer border-b border-border/50 p-[1rem]",
          isSelected ? "bg-grey-blue-light" : "bg-white",
          watchMode ? "pointer-events-none" : "pointer-events-auto"
        )}
        onClick={() => {
          if (watchMode) return;
          onSelect?.();
        }}
      >
        <div className="flex items-start gap-4">
          <span className="shrink-0 font-semibold">{cartItem.quantity}x</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4 font-semibold">
              <span className="min-w-0 break-words">
                {getFieldByLang(cartItem?.name) || "Geen product naam"}
              </span>
              <span className="shrink-0">{formatPrice(total)}</span>
            </div>

            {cartItem?.selected_sub_content && (
              <div className="text-xl font-light italic">
                {cartItem?.selected_sub_content?.map((tp, i, array) => (
                  <span key={i}>
                    {`${tp.name}`}
                    {array.length !== i + 1 ? `, ` : "."}
                  </span>
                ))}
              </div>
            )}

            {!watchMode && cartItem.note && (
              <div
                className={cn(
                  "mt-2 inline-flex max-w-full items-start gap-2 rounded-lg px-3 py-[0.4rem] text-small leading-[2rem] text-text-secondary",
                  isSelected ? "bg-white" : "bg-grey"
                )}
                onClick={(e) => {
                  if (!isSelected) return;
                  e.stopPropagation();
                  setModal("cartItemNote", { index, cartItem });
                }}
              >
                <Icon
                  className="mt-[0.3rem] h-[1.4rem] w-[1.4rem] shrink-0 fill-text-tertiary"
                  variants="comment"
                />
                <span className="min-w-0 whitespace-pre-wrap break-words">
                  {cartItem.note}
                </span>

                <button
                  type="button"
                  aria-label={t("posOrders.delete")}
                  className="relative ml-1 flex h-[2rem] w-[2rem] shrink-0 cursor-pointer items-center justify-center rounded-full bg-grey-blue/40 after:absolute after:-inset-2 after:content-[''] active:bg-grey-blue/70"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteNote?.();
                  }}
                >
                  <Icon
                    className="h-[1.2rem] w-[1.2rem] fill-dark"
                    variants="times"
                  />
                </button>
              </div>
            )}
          </div>
        </div>

        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
          className={cn(
            "mt-2 flex gap-2 xl:gap-3 overflow-hidden transition-[height,opacity] duration-200 ease-in-out",
            isSelected && !watchMode
              ? "h-14 opacity-100"
              : "h-0 opacity-0 pointer-events-none"
          )}
        >
          <Center
            className="flex-1 bg-grey rounded-md"
            onClick={() => onDelete?.()}
          >
            <Icon className="h-7 w-7 fill-dark" variants="minus" />
          </Center>
          <Center
            className="flex-1 bg-grey rounded-md"
            onClick={() => onAdd?.()}
          >
            <Icon className="h-7 w-7 fill-dark" variants="plus" />
          </Center>
          <Center
            className="flex-1 bg-grey rounded-md"
            onClick={() => setModal("cartItemNote", { index, cartItem })}
          >
            <Icon className="h-7 w-7 fill-dark" variants="comment" />
          </Center>
          {cartItem.sub_content?.length > 0 && (
            <Center
              className="flex-1 bg-grey rounded-md"
              onClick={() => {
                setModal("posOrderSelectToppings", {
                  isEdit: true,
                  index: index,
                  product: cartItem,
                });
              }}
            >
              <Icon className="h-7 w-7 fill-dark" variants="basket" />
            </Center>
          )}
          <Center
            className="flex-1 bg-grey rounded-md"
            onClick={() =>
              setModal("posOrderProduct", {
                product: { ...cartItem, parent_id: cartItem.parent_id },
                index,
              })
            }
          >
            <Icon className="h-8 w-8 fill-dark" variants="edit" />
          </Center>
        </div>
      </div>
    );
  }
);

export default CartItem;
